import { useMemo } from 'react';

import { useProjectArtifactsManifest } from '../lib/api/useProjectArtifactsManifest';

/**
 * Lowercased names of the project's analytical datasets, for client-side
 * duplicate-name validation in the copy/rename dialogs — so a clashing name is
 * caught inline (the user fixes it before submitting) instead of bouncing back
 * as a server 409. Reads the already-loaded artifacts manifest (cached, no extra
 * fetch). Pass ``excludeProfileId`` to ignore the dataset being renamed.
 *
 * The server uniqueness check remains the authoritative guard (covers the race
 * where two datasets are created with the same name concurrently).
 */
export const useExistingAnalyticalDatasetNames = (
  projectId: string,
  excludeProfileId?: string
): Set<string> => {
  const { entries } = useProjectArtifactsManifest(projectId);
  return useMemo(() => {
    const names = new Set<string>();
    for (const entry of entries) {
      if (entry.kind !== 'analytical_dataset') continue;
      if (excludeProfileId && entry.id === excludeProfileId) continue;
      names.add((entry.name ?? '').trim().toLowerCase());
    }
    return names;
  }, [entries, excludeProfileId]);
};
